import React from "react";
import DOMPurify from "dompurify";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { apiService } from "../../services";
import type { IArticle, ITicketFile } from "../../types/tickets";
import { resolveCitationHref } from "../../utils/backendUrl";

interface ArticleCardProps {
  article: IArticle;
  ticketId: number;
  /** Files the support form linked from this article's body. */
  linkedFiles?: ITicketFile[];
  formatDateTime: (d: string) => string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  ticketId,
  linkedFiles = [],
  formatDateTime,
}) => {
  const theme = useTheme();
  const isAgent = article.sender === "Agent" || article.type === "note";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderLeft: `3px solid ${isAgent ? theme.palette.primary.main : theme.palette.grey[400]}`,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">
          {article.from || article.sender || "Unknown"}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {formatDateTime(article.created_at)}
        </Typography>
      </Box>

      {article.content_type === "text/html" ? (
        <Box
          sx={{
            "& img": { maxWidth: "100%" },
            "& a": { color: theme.palette.primary.main },
            wordBreak: "break-word",
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(article.body),
          }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {article.body}
        </Typography>
      )}

      {article.attachments.length > 0 || linkedFiles.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Attachments
          </Typography>
          {article.attachments.map((att) => (
            <Box key={att.id} sx={{ mt: 0.5 }}>
              <a
                href={apiService.getTicketAttachmentUrl(ticketId, article.id, att.id)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: theme.palette.primary.main, fontSize: "0.875rem" }}
              >
                {att.filename} ({att.size})
              </a>
            </Box>
          ))}
          {linkedFiles.map((file) => (
            <Box key={file.id} sx={{ mt: 0.5 }}>
              <a
                href={resolveCitationHref(file.download_url)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: theme.palette.primary.main, fontSize: "0.875rem" }}
              >
                {file.filename}
              </a>
            </Box>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
};

export default ArticleCard;
